/**
 * Jurídico: gestão de contratos e demandas jurídicas externas (ADR 0025). Validações usadas na
 * tela e no servidor, e a leitura da planilha de contratos exportada do SharePoint.
 */
import { z } from 'zod';
import { LEGAL_COMPARTMENTS, LEGAL_CONFIDENTIALITY } from './legal.ts';
import { costAllocationInput, costAllocationIssues } from './cost-allocation.ts';
import { structuredTermsInput } from './payment-terms.ts';

export const CONTRACT_KINDS = ['main', 'amendment', 'renewal', 'termination'] as const;
export const CONTRACT_CATEGORIES = [
  'services',
  'rental',
  'saas',
  'partnership',
  'licensing',
  'supply',
  'artist',
  'influencer',
  'nda',
  'other',
] as const;
export const CONTRACT_STATUSES = [
  'draft',
  'signing',
  'active',
  'inactive',
  'terminated',
  'cancelled',
  /** Importado sem situação na planilha: o Jurídico confere e escolhe a situação real. */
  'unconfirmed',
] as const;
export const CONTRACT_PERIODICITIES = [
  'one_time',
  'monthly',
  'quarterly',
  'semiannual',
  'yearly',
  'on_demand',
  'other',
] as const;
export const CONTRACT_COST_ALLOCATIONS = ['departmental', 'shared'] as const;
export const RECORD_DOC_TYPES = [
  'signed',
  'draft',
  'proposal',
  'report',
  'invoice',
  'other',
  // Contencioso: comprovantes (protocolos, pagamentos) e atas de audiência.
  'receipt',
  'minutes',
  // Pirataria: evidências (capturas, páginas salvas, compras de teste).
  'evidence',
] as const;
export const EXTERNAL_CATEGORIES = [
  'litigation',
  'labor',
  'tax',
  'corporate',
  'contracts',
  'trademarks_patents',
  'privacy',
  'compliance',
  'consulting',
  'other',
] as const;
export const EXTERNAL_STATUSES = [
  'open',
  'in_progress',
  'waiting',
  'completed',
  'cancelled',
] as const;
export const EXTERNAL_COST_KINDS = ['fee', 'cost', 'expense'] as const;
export const EXTERNAL_COST_STATUSES = ['estimated', 'incurred'] as const;
/** Dias para "próximo do vencimento" nos indicadores. */
export const CONTRACT_EXPIRING_DAYS = [30, 60, 90] as const;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const amount = z
  .string()
  .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números com ponto (ex.: 1500.00)')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const currency = z
  .string()
  .regex(/^([A-Z]{3})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const legalContractInput = z
  .object({
    kind: z.enum(CONTRACT_KINDS).default('main'),
    parentId: id,
    legalEntityId: z.uuid(),
    partnerId: id,
    counterpartyName: z.string().trim().min(1).max(300),
    counterpartyTaxId: text(40),
    externalNumber: text(60),
    title: z.string().trim().min(1).max(300),
    category: z.enum(CONTRACT_CATEGORIES).default('other'),
    description: text(10000),
    status: z.enum(CONTRACT_STATUSES).default('active'),
    startDate: day,
    endDate: day,
    autoRenewal: z.boolean().default(false),
    noticeDays: z.number().int().min(0).max(3650).nullable().optional(),
    amount,
    currency,
    periodicity: z
      .enum(CONTRACT_PERIODICITIES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    costAllocation: z
      .enum(CONTRACT_COST_ALLOCATIONS)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    /**
     * Condição de pagamento estruturada (ADR 0104, D4). No aditivo ou na renovação, vale para as notas
     * lançadas depois dele; o título já lançado não muda.
     */
    terms: structuredTermsInput.nullable().optional(),
    departmentId: id,
    costCenterId: id,
    businessOwnerId: id,
    /** Gestor pelo cadastro oficial de colaboradores (Dados Mestres), com ou sem login. */
    businessOwnerEmployeeId: id,
    businessOwnerName: text(200),
    /** Rateio entre centros de custo oficiais (formato comum; com rateio, soma 100%). */
    allocation: costAllocationInput.nullable().optional(),
    legalOwnerId: id,
    requestId: id,
    confidentiality: z.enum(LEGAL_CONFIDENTIALITY).default('internal'),
    compartment: z.enum(LEGAL_COMPARTMENTS).nullable().optional(),
    notes: text(10000),
  })
  .refine((v) => (v.amount == null) === (v.currency == null), {
    path: ['currency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'O fim não pode ser antes do início',
  })
  .refine((v) => v.kind !== 'termination' || !v.terms, {
    path: ['terms'],
    message: 'Distrato não tem condição de pagamento',
  })
  .refine((v) => (v.kind === 'main') === !v.parentId, {
    path: ['parentId'],
    message: 'Aditivo, renovação e distrato precisam do contrato principal',
  })
  .superRefine((v, ctx) => {
    for (const issue of costAllocationIssues(v.allocation, false)) {
      ctx.addIssue({ code: 'custom', path: issue.path.split('.'), message: issue.message });
    }
  });
export type LegalContractInput = z.infer<typeof legalContractInput>;

/** Ligações confirmadas na conciliação: a outra parte (parceiro) e/ou o gestor (colaborador). */
export const legalContractLinksInput = z.object({
  links: z
    .array(
      z
        .object({
          contractId: z.uuid(),
          partnerId: z.uuid().nullable().optional(),
          employeeId: z.uuid().nullable().optional(),
        })
        .refine((l) => !!l.partnerId || !!l.employeeId, 'Escolha o parceiro ou o colaborador'),
    )
    .min(1)
    .max(500),
});

/**
 * Pendências de um contrato (contratos históricos e importados ficam sinalizados para a equipe
 * jurídica regularizar; rascunho e cancelado não entram): outra parte fora do cadastro de
 * parceiros, gestor sem colaborador oficial, centro de custo master (só quando há valor:
 * instrumento sem natureza financeira não recebe centro fictício), rateio declarado sem regra e
 * situação "a confirmar" da carga. Contrato com rateio também precisa do master (ADR 0030, 0350).
 */
export const CONTRACT_PENDING_LINKS = [
  'partner',
  'owner',
  'costCenter',
  'allocation',
  'status',
] as const;
export function contractPendingLinks(c: {
  partnerId: string | null;
  businessOwnerEmployeeId: string | null;
  costCenterId: string | null;
  costAllocation: string | null;
  allocation: { split: boolean; lines: unknown[] } | null;
  amount: string | null;
  status: string;
  /** Há regra de rateio ativa (0350). */
  hasAllocationRule?: boolean;
}): (typeof CONTRACT_PENDING_LINKS)[number][] {
  if (['draft', 'cancelled'].includes(c.status)) return [];
  const out: (typeof CONTRACT_PENDING_LINKS)[number][] = [];
  if (!c.partnerId) out.push('partner');
  if (!c.businessOwnerEmployeeId) out.push('owner');
  const lines = c.allocation?.lines.length ?? 0;
  if (c.amount && !c.costCenterId && lines === 0) out.push('costCenter');
  if (
    c.costAllocation === 'shared' &&
    !c.hasAllocationRule &&
    !(c.allocation?.split && lines >= 2)
  ) {
    out.push('allocation');
  }
  if (c.status === 'unconfirmed') out.push('status');
  return out;
}

export const legalExternalMatterInput = z.object({
  legalEntityId: z.uuid(),
  providerId: z.uuid(),
  category: z.enum(EXTERNAL_CATEGORIES),
  subject: z.string().trim().min(1).max(300),
  description: text(10000),
  internalOwnerId: id,
  externalOwner: text(200),
  priority: z.enum(['normal', 'high', 'critical']).default('normal'),
  dueDate: day,
  status: z.enum(EXTERNAL_STATUSES).default('open'),
  requestId: id,
  contractId: id,
  ipReference: text(100),
  confidentiality: z.enum(LEGAL_CONFIDENTIALITY).default('internal'),
  compartment: z.enum(LEGAL_COMPARTMENTS).nullable().optional(),
});
export type LegalExternalMatterInput = z.infer<typeof legalExternalMatterInput>;

export const legalExternalCostInput = z.object({
  kind: z.enum(EXTERNAL_COST_KINDS),
  status: z.enum(EXTERNAL_COST_STATUSES).default('estimated'),
  description: z.string().trim().min(1).max(500),
  amount: z.string().regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números com ponto (ex.: 1500.00)'),
  currency: z.string().regex(/^[A-Z]{3}$/),
  incurredOn: day,
});

/**
 * Escolhas feitas no assistente de importação para linhas com dúvida, por número da linha da
 * planilha: a empresa (quando o nome não é idêntico ao cadastro) e o parceiro sugerido.
 */
export const importResolutions = z
  .record(
    z.string().regex(/^\d{1,6}$/),
    z.object({
      legalEntityId: z.uuid().optional(),
      partnerId: z.uuid().nullable().optional(),
    }),
  )
  .default({});
export type ImportResolutions = z.infer<typeof importResolutions>;

export const legalContractImportFile = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentBase64: z.string().min(1).max(14_000_000),
  resolutions: importResolutions,
});
export const legalContractImportApply = legalContractImportFile.extend({
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

// ----- Planilha de contratos do SharePoint -----

/** Colunas da planilha exportada do SharePoint (Relação de Contratos). */
export const SHAREPOINT_CONTRACT_COLUMNS = {
  entity: 'Empresa',
  taxId: 'CNPJ',
  supplier: 'Fornecedor',
  number: 'Nº Contrato',
  status: 'Status do Contrato',
  start: 'Data Ínicio',
  end: 'Data Fim',
  departmentManager: 'Gestor Departamento',
  department: 'Departamento',
  area: 'Área ou Departamento',
  businessOwner: 'Gestor Contratual',
  costCenter: 'Centro de Custo',
  classificationType: 'Tipo de Classificação',
  expenseType: 'Tipo de Gasto',
  costType: 'Tipo de Custo',
  allocation: 'Tipo de Contrato',
  amount: 'Valor PrevistoMensal',
  currency: 'Moeda',
  periodicity: 'Periodicidade',
  description: 'Descrição Detalhada',
  object: 'Objeto',
  notes: 'Observações',
  paymentMonths: 'Mês para Pagamento',
  paymentNotes: 'Pagamento Efetuado?',
} as const;

/** Texto sem acento, minúsculo e com espaços simples (para comparar nomes da planilha). */
export function looseText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Data da planilha: número de série do Excel, dd/mm/aaaa ou aaaa-mm-dd. */
export function sheetDate(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (/^\d{4,6}(\.\d+)?$/.test(v)) {
    const serial = Math.floor(Number(v));
    // O Excel conta dias desde 30/12/1899.
    const ms = Date.UTC(1899, 11, 30) + serial * 86_400_000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
  if (br) return `${br[3]}-${br[2]!.padStart(2, '0')}-${br[1]!.padStart(2, '0')}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

/** Valor da planilha em texto decimal com até 4 casas (nunca ponto flutuante gravado). */
export function sheetAmount(value: string | null | undefined): string | null {
  const v = (value ?? '').trim().replace(/\s/g, '');
  if (!v) return null;
  // Formato brasileiro (1.234,56) ou com ponto decimal (2447.95), como vem do SharePoint.
  const normal = v.includes(',') ? v.replace(/\./g, '').replace(',', '.') : v;
  if (!/^\d+(\.\d+)?$/.test(normal)) return null;
  const [int, dec = ''] = normal.split('.');
  return `${int}.${(dec + '0000').slice(0, 4)}`;
}

/** Moeda da planilha ("BRL - BRASIL") no código de três letras. */
export function sheetCurrency(value: string | null | undefined): string | null {
  const m = /^\s*([A-Za-z]{3})\b/.exec(value ?? '');
  return m ? m[1]!.toUpperCase() : null;
}

const PERIODICITY: Record<string, (typeof CONTRACT_PERIODICITIES)[number]> = {
  mensal: 'monthly',
  trimestral: 'quarterly',
  semestral: 'semiannual',
  anual: 'yearly',
  'sob demanda': 'on_demand',
  unica: 'one_time',
  'pagamento unico': 'one_time',
};
export function sheetPeriodicity(
  value: string | null | undefined,
): (typeof CONTRACT_PERIODICITIES)[number] | null {
  const v = looseText(value ?? '');
  if (!v) return null;
  return PERIODICITY[v] ?? 'other';
}

const STATUS: Record<string, (typeof CONTRACT_STATUSES)[number]> = {
  ativo: 'active',
  inativo: 'inactive',
  cancelado: 'cancelled',
  encerrado: 'terminated',
  distratado: 'terminated',
};
export function sheetStatus(
  value: string | null | undefined,
): (typeof CONTRACT_STATUSES)[number] | null {
  return STATUS[looseText(value ?? '')] ?? null;
}

const CATEGORY: Record<string, (typeof CONTRACT_CATEGORIES)[number]> = {
  'prestacao de servico': 'services',
  'prestacao de servicos': 'services',
  'locacao de imovel': 'rental',
  saas: 'saas',
  parceria: 'partnership',
  licenciamento: 'licensing',
  fornecimento: 'supply',
  artista: 'artist',
  artist: 'artist',
};
/**
 * Categoria pelo "Objeto". No SharePoint a coluna aceita mais de uma escolha ("A;#B"): artista
 * vence (é o que muda o tratamento do contrato); senão vale a primeira escolha conhecida.
 */
export function sheetCategory(
  value: string | null | undefined,
): (typeof CONTRACT_CATEGORIES)[number] {
  const choices = (value ?? '')
    .split(';#')
    .map((v) => CATEGORY[looseText(v)])
    .filter((c) => c !== undefined);
  return choices.includes('artist') ? 'artist' : (choices[0] ?? 'other');
}

export function sheetAllocation(
  value: string | null | undefined,
): (typeof CONTRACT_COST_ALLOCATIONS)[number] | null {
  const v = looseText(value ?? '');
  return v === 'rateado' ? 'shared' : v === 'departamental' ? 'departmental' : null;
}

/** Código do centro de custo na planilha ("01060627027 - PAYROLL DEPARTMENT" → "01060627027"). */
export function sheetCostCenterCode(value: string | null | undefined): string | null {
  const m = /^\s*([A-Za-z0-9_-]+)\s*-/.exec(value ?? '');
  return m ? m[1]!.toUpperCase() : (value ?? '').trim().toUpperCase() || null;
}
